import { useCallback } from 'react';
import { useHttpClient } from '../../client/HttpClientContext';
import { useObjectId } from './useObjectId';
import { cache as propertyValuesCache } from '../../values/PropertyValuesCache';
import { useObjectTypeId } from './useObjectTypeId';
import { useAsyncFunction } from '../../utils/hooks/useAsyncFunction';
import { useProperty } from './useProperty';

/**
 * Hook that returns the sourceId for a property value from the property values cache.
 *
 * IMPORTANT: This hook only returns sourceId values for properties that are already
 * in the PropertyValuesCache, which as of July 2025, is only populated on pages
 * that render a properties form via <NewPropertiesForm /> or useNewPropertiesForm().
 *
 * This behavior is intentional to avoid making additional API calls, as making this
 * request on every property input would be very expensive on the index page, for example.
 * That would cause one additional request per record.
 *
 * @returns AsyncState containing the sourceId string or null if loading / not in the cache
 */
export default function useSourceId(options = {}) {
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const httpClient = useHttpClient();
  const property = useProperty();
  const getSourceId = useCallback(async () => {
    var _currentCachedValues$;
    if (!objectId || !(property !== null && property !== void 0 && property.name)) {
      return null;
    }
    const cacheKey = {
      objectTypeId,
      objectId
    };
    if (!propertyValuesCache.has(cacheKey)) {
      return null;
    }
    const currentCachedValues = await propertyValuesCache.get(cacheKey, httpClient);
    return (currentCachedValues === null || currentCachedValues === void 0 || (_currentCachedValues$ = currentCachedValues[property.name]) === null || _currentCachedValues$ === void 0 ? void 0 : _currentCachedValues$.sourceId) || null;
  }, [objectId, objectTypeId, property, httpClient]);
  return useAsyncFunction(getSourceId, Object.assign({}, options));
}